/**
 * aiwa-filter-client.js — client-side multi-facet filtering.
 *
 * Why this exists: Search & Discovery isn't installed on this store, so
 * collection.filters never exposes a tag facet (filter.p.tag stays dead).
 * The product grid also stays server-paginated (section.settings.products_per_page),
 * so real multi-select AND filtering needs the FULL product set, not just the
 * current page — fetched from sections/main-collection-facets.liquid via
 * ?view=facets&page=N (see that file's header comment for the JSON shape).
 *
 * Behavior:
 *   - No filters active  → original server-rendered, paginated grid is left
 *     alone untouched (normal browsing/pagination keeps working as today).
 *   - Any filter active  → grid is replaced with every matching product across
 *     the whole collection (unpaginated — filtered sets are small enough that
 *     paging them would just add friction).
 *   - Counts next to each option always reflect "if I also picked this,
 *     how many would match" (standard facet UX), computed against the OTHER
 *     active facets, not the currently-filtered set.
 *
 * Card markup is rebuilt to match sections/main-collection-ai.liquid's
 * .aiwa-product-card structure exactly, so the existing event-delegated
 * favorite/add-to-cart handlers (document-level listeners in that section)
 * keep working on client-built cards with zero changes needed there.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-aiwa-facet-root]');
  if (!root) return;

  var collectionUrl = root.getAttribute('data-collection-url');
  if (!collectionUrl) return;

  // The Arabic storefront renders collection.url without its /ar prefix (and
  // Shopify.routes.root is "/" there too), so take the prefix from the page's
  // own path, or the product data (names, prices, links) comes back in English.
  var localePrefix = (window.location.pathname.match(/^(.*?)\/collections\//) || [])[1] || '';
  if (localePrefix && collectionUrl.indexOf(localePrefix + '/') !== 0) collectionUrl = localePrefix + collectionUrl;

  var isAr = document.documentElement.getAttribute('lang') === 'ar'
    || document.documentElement.getAttribute('dir') === 'rtl';

  var FACET_GROUPS = ['occasion', 'colour', 'flower', 'price', 'recipient'];
  var PRICE_BANDS = ['Under 200 AED', '200-400 AED', '400-800 AED', 'Over 800 AED'];

  // Tag-bar pill (e.g. /collections/flower-bouquets/roses): Shopify narrows
  // the server grid by the tag handle in the URL path, but the facet data is
  // the whole collection, so the same narrowing has to be applied here or a
  // filter picked under "Roses" shows every matching product in the collection.
  function handleize(s) {
    return String(s || '').trim().toLowerCase()
      .replace(/['"]/g, '').replace(/[^a-z0-9]+/g, '-')
      .replace(/-+/g, '-').replace(/^-|-$/g, '');
  }

  function activePillHandle() {
    var m = window.location.pathname.match(/\/collections\/[^\/]+\/([^\/?#]+)/);
    return m ? handleize(decodeURIComponent(m[1])) : '';
  }

  function inPill(product, pill) {
    if (!pill) return true;
    var hs = product._tagHandles || (product._tagHandles = product.tags.map(handleize));
    return hs.indexOf(pill) !== -1;
  }

  // ---- panel MODE switch: FILTERS vs SORT are two distinct views of the
  // same physical drawer (Anna: "1 button for filters and 1 button for
  // sorting pls dont mix them"). The sort <input>s must stay inside
  // [data-aiwa-filter-panel] for collection-ai-filters.js's legacy AJAX to
  // keep working (see aiwa-filter-drawer.liquid's top comment) — so instead
  // of two separate drawers, one panel toggles which data-aiwa-section is
  // visible, driven by which trigger (data-aiwa-panel-mode="filters"|"sort")
  // was clicked. CSS rules live in assets/collection-ai.css.
  (function () {
    var panelEl = document.querySelector('[data-aiwa-filter-panel]');
    if (!panelEl) return;

    var headerPrefixEl = document.querySelector('[data-aiwa-header-prefix]');
    var applyBtnEl = document.querySelector('[data-aiwa-apply]');
    var sortDetailsEl = panelEl.querySelector('[data-aiwa-section="sort"]');

    function setPanelMode(mode) {
      var isSort = mode === 'sort';
      panelEl.classList.toggle('aiwa-mode-sort', isSort);
      if (headerPrefixEl) {
        headerPrefixEl.textContent = isSort
          ? headerPrefixEl.getAttribute('data-label-sort')
          : headerPrefixEl.getAttribute('data-label-filters');
      }
      if (applyBtnEl) {
        applyBtnEl.textContent = isSort
          ? applyBtnEl.getAttribute('data-label-sort')
          : applyBtnEl.getAttribute('data-label-filters');
      }
      // Force-expand in case the shopper had manually collapsed it on a
      // previous visit to this mode.
      if (isSort && sortDetailsEl) sortDetailsEl.open = true;
    }

    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-aiwa-panel-mode]');
      if (!trigger) return;
      setPanelMode(trigger.getAttribute('data-aiwa-panel-mode'));
    });

    // Sort is single-choice and already auto-applies via the existing
    // legacy AJAX (collection-ai-filters.js's document-level change
    // watcher, untouched here) — close the panel right after picking one
    // so Sort behaves like "pick one, done" instead of needing a second
    // tap on a button that (in sort mode) has nothing left to "apply".
    panelEl.addEventListener('change', function (e) {
      if (!e.target.matches('input[name="sort_by"]')) return;
      panelEl.classList.remove('is-open');
      var overlay = document.querySelector('[data-aiwa-filter-overlay]');
      if (overlay) overlay.classList.remove('is-open');
      document.body.classList.remove('aiwa-filter-open');
    });
  })();

  // Shared with the capture-phase apply-button guard below — must be
  // reachable outside init()'s closure since that guard has to work the
  // instant a facet is checked, not just after init() finishes.
  var facetsCurrentlyActive = false;

  function priceBand(price) {
    var p = parseFloat(String(price).replace(/,/g, '')) || 0;
    if (p < 200) return PRICE_BANDS[0];
    if (p < 400) return PRICE_BANDS[1];
    if (p < 800) return PRICE_BANDS[2];
    return PRICE_BANDS[3];
  }

  function matches(product, group, value) {
    if (group === 'price') return priceBand(product.price) === value;
    var lc = product._tagsLc || (product._tagsLc = product.tags.map(function (t) { return String(t).toLowerCase(); }));
    return lc.indexOf(String(value).toLowerCase()) !== -1;
  }

  // ---- data loading, cached in sessionStorage for 30 min per collection ----
  var CACHE_TTL_MS = 30 * 60 * 1000;

  function currentSort() {
    var checked = document.querySelector('[data-aiwa-filter-panel] input[name="sort_by"]:checked');
    if (checked) return checked.value;
    return new URLSearchParams(window.location.search).get('sort_by') || '';
  }

  var dataSort = currentSort();

  function cacheKey() {
    return 'aiwa_facets::' + collectionUrl + '::' + dataSort;
  }

  function loadCache() {
    try {
      var raw = sessionStorage.getItem(cacheKey());
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || (Date.now() - parsed.ts) > CACHE_TTL_MS) return null;
      return parsed.products;
    } catch (e) {
      return null;
    }
  }

  function saveCache(products) {
    try {
      sessionStorage.setItem(cacheKey(), JSON.stringify({ ts: Date.now(), products: products }));
    } catch (e) {
      /* sessionStorage full/unavailable — not fatal, just no cache */
    }
  }

  function fetchPageOnce(page, attempt) {
    var url = collectionUrl + '?view=facets&page=' + page + (dataSort ? '&sort_by=' + encodeURIComponent(dataSort) : '') +
      (attempt ? '&_r=' + attempt + '-' + Date.now() : '');
    return fetch(url, { credentials: 'same-origin', cache: 'no-store' }).then(function (res) {
      if (!res.ok) throw new Error('facets fetch ' + res.status);
      return res.text().then(function (text) {
        // Defensive: `shopify theme dev`'s local proxy wraps alternate JSON
        // template output in a <div id="shopify-section-..."> for its own
        // hot-reload bookkeeping (confirmed against real Shopify servers,
        // which return this endpoint as clean JSON with no wrapper — this
        // only happens on the local dev proxy). Extracting the outermost
        // {...} rather than assuming byte 0 is "{" costs nothing on the
        // clean-JSON path and makes local dev-proxy testing work too.
        var start = text.indexOf('{');
        var end = text.lastIndexOf('}');
        if (start === -1 || end === -1) throw new Error('facets response had no JSON payload');
        return JSON.parse(text.slice(start, end + 1));
      });
    });
  }

  // Cheap defensive backstop for a real transient network hiccup — not a
  // workaround for a known bug, just standard fetch resilience.
  // Shopify occasionally aborts this 250-product render part-way
  // ("Liquid error ... internal"), leaving truncated JSON. Retry up to 3 more
  // times with a cache-busting param so a broken cached copy isn't reused.
  var RETRY_DELAYS_MS = [300, 900, 2000];
  function fetchPage(page) {
    function attempt(n) {
      return fetchPageOnce(page, n).catch(function (err) {
        if (n >= RETRY_DELAYS_MS.length) throw err;
        return new Promise(function (resolve) { setTimeout(resolve, RETRY_DELAYS_MS[n]); }).then(function () {
          return attempt(n + 1);
        });
      });
    }
    return attempt(0);
  }

  function fetchAllProducts() {
    var cached = loadCache();
    if (cached) return Promise.resolve(cached);

    return fetchPage(1).then(function (first) {
      var all = (first.products || []).slice();
      var pages = first.pages || 1;
      if (pages <= 1) {
        saveCache(all);
        return all;
      }
      var rest = [];
      for (var p = 2; p <= pages; p++) rest.push(fetchPage(p));
      return Promise.all(rest).then(function (chunks) {
        chunks.forEach(function (c) { all = all.concat(c.products || []); });
        saveCache(all);
        return all;
      });
    }).catch(function (err) {
      console.error('[aiwa-filter] could not load product data:', err);
      return [];
    });
  }

  // ---- card rendering (mirrors sections/main-collection-ai.liquid markup) ----
  function badgeFor(product) {
    if (product.tags.indexOf('Coming Back Soon') !== -1) {
      return { cls: 'aiwa-badge--coming-soon', text: isAr ? 'قريباً' : 'Coming Back Soon' };
    }
    if (product.tags.indexOf('Same-Day Delivery') !== -1) {
      return { cls: 'aiwa-badge--same-day', text: isAr ? 'نفس اليوم' : 'Same Day' };
    }
    if (product.tags.indexOf('Next-Day Delivery') !== -1) {
      return { cls: 'aiwa-badge--next-day', text: isAr ? 'اليوم التالي' : 'Next Day' };
    }
    return null;
  }

  function escapeHtml(s) {
    var div = document.createElement('div');
    div.textContent = s == null ? '' : String(s);
    return div.innerHTML;
  }

  function buildCard(product) {
    var badge = badgeFor(product);
    var favLabel = isAr ? 'أضف إلى المفضلة' : 'Add to favorites';
    var cartLabel = isAr ? 'أضف إلى السلة' : 'Add to cart';

    var li = document.createElement('li');
    li.className = 'grid__item';
    li.setAttribute('data-aiwa-client-card', '');

    li.innerHTML =
      '<a href="' + escapeHtml(product.url) + '" class="aiwa-product-card"' +
      ' data-product-handle="' + escapeHtml(product.handle) + '"' +
      ' data-first-variant="' + escapeHtml(product.variantId) + '"' +
      ' data-has-multiple-variants="' + (product.hasMultipleVariants ? 'true' : 'false') + '"' +
      ' data-product-title="' + escapeHtml(product.title) + '"' +
      ' data-product-image="' + escapeHtml(product.image) + '"' +
      ' data-product-price="' + escapeHtml(product.money) + '">' +
        '<div class="aiwa-product-media">' +
          (badge ? '<div class="aiwa-product-badge ' + badge.cls + '">' + escapeHtml(badge.text) + '</div>' : '') +
          '<button type="button" class="aiwa-card-heart" data-aiwa-favorite data-product-handle="' + escapeHtml(product.handle) + '" aria-pressed="false" aria-label="' + favLabel + '">' +
            '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M12 20.5s-7-4.6-7-10.2C5 7.5 7.1 5.5 9.6 5.5c1.5 0 2.7.8 3.4 2.1.7-1.3 1.9-2.1 3.4-2.1 2.5 0 4.6 2 4.6 4.8 0 5.6-7 10.2-7 10.2z" stroke="#1a1a1a" stroke-width="1.4" fill="none" stroke-linejoin="round"/></svg>' +
          '</button>' +
          (product.image ? '<img src="' + escapeHtml(product.image) + '" alt="' + escapeHtml(product.imageAlt || product.title) + '" loading="lazy" width="600">' : '') +
          '<button type="button" class="aiwa-product-add" data-aiwa-add-to-cart aria-label="' + cartLabel + '"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#111" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M16 11V7a4 4 0 0 0-8 0v4"/><path d="M5 9h14l1 12H4L5 9z"/></svg></button>' +
        '</div>' +
        '<div class="aiwa-product-name">' + escapeHtml(product.title) + '</div>' +
        '<div class="aiwa-product-price-row"><span class="aiwa-product-price">' + escapeHtml(product.money) + '</span></div>' +
      '</a>';

    return li;
  }

  // ---- main controller, boots once product data is loaded ----
  function init(products) {
    var state = { occasion: [], colour: [], flower: [], price: [], recipient: [] };
    var checkboxes = Array.prototype.slice.call(root.querySelectorAll('input[data-facet-group]'));
    var headerCountEl = document.querySelector('[data-aiwa-header-count]');
    var originalHeaderCount = headerCountEl ? headerCountEl.textContent : '';
    var anyActive = false;
    var originalGridStale = false;

    // Sort changes (still server-driven, unchanged) go through the existing
    // AJAX section-swap in collection-ai-filters.js, which replaces
    // #CollectionAIProductGrid's *children* in place — the outer container
    // node survives, but the .product-grid <ul> and pagination <div>s inside
    // it get swapped for fresh ones. Caching references to those inner nodes
    // once here would go stale the moment Sort runs with no facet active
    // (the one case the apply-button guard above still lets through). So
    // grid/pagination are looked up fresh on every renderGrid() call instead.
    function gridContainerEl() { return document.querySelector('#CollectionAIProductGrid'); }
    function originalGridEl() { return document.querySelector('#CollectionAIProductGrid .product-grid'); }
    // NOTE: #CollectionAIPagination is (pre-existingly) duplicated in the
    // section markup — querySelectorAll catches both instances so neither is
    // left visible under a client-rendered filtered grid.
    function paginationEls() { return document.querySelectorAll('#CollectionAIPagination'); }

    function isFacetActive() {
      return FACET_GROUPS.some(function (g) { return state[g].length > 0; });
    }

    function computeVisible(overrideGroup) {
      var pill = activePillHandle();
      return products.filter(function (p) {
        if (!inPill(p, pill)) return false;
        return FACET_GROUPS.every(function (g) {
          if (g === overrideGroup) return true;
          var selected = state[g];
          if (!selected.length) return true;
          return selected.some(function (v) { return matches(p, g, v); });
        });
      });
    }

    function updateCounts() {
      checkboxes.forEach(function (cb) {
        var group = cb.getAttribute('data-facet-group');
        var value = cb.getAttribute('data-facet-value');
        var baseline = computeVisible(group);
        var count = baseline.filter(function (p) { return matches(p, group, value); }).length;
        var option = cb.closest('[data-aiwa-facet-option]');
        var countEl = option && option.querySelector('[data-facet-count]');
        if (countEl) countEl.textContent = '(' + count + ')';
        var isChecked = state[group].indexOf(value) !== -1;
        cb.disabled = count === 0 && !isChecked;
        if (option) option.classList.toggle('is-disabled', cb.disabled);
      });
    }

    function renderGrid() {
      anyActive = isFacetActive();
      facetsCurrentlyActive = anyActive;

      if (!anyActive) {
        // back to normal: restore the original server-rendered grid + pagination
        var existingOriginal = originalGridEl();
        if (existingOriginal && existingOriginal.hasAttribute('data-aiwa-hidden')) {
          existingOriginal.removeAttribute('data-aiwa-hidden');
          existingOriginal.style.display = '';
        }
        var clientGrid = document.querySelector('[data-aiwa-client-grid]');
        if (clientGrid) clientGrid.remove();
        paginationEls().forEach(function (el) { el.style.display = ''; });
        if (headerCountEl) headerCountEl.textContent = originalHeaderCount;
        // Sort was changed while a filter was on: the server grid behind the
        // client grid is still in the old order, so reload it now.
        if (originalGridStale && typeof window.aiwaLoadCollectionSection === 'function') {
          originalGridStale = false;
          if (typeof window.aiwaResetInfiniteScrollState === 'function') window.aiwaResetInfiniteScrollState();
          window.aiwaLoadCollectionSection(window.location.pathname + window.location.search);
        }
        return;
      }

      var visible = computeVisible(null);

      // hide the original grid + its pagination while a filter is active
      var originalGrid = originalGridEl();
      if (originalGrid) {
        originalGrid.setAttribute('data-aiwa-hidden', '');
        originalGrid.style.display = 'none';
      }
      paginationEls().forEach(function (el) { el.style.display = 'none'; });

      var clientGrid = document.querySelector('[data-aiwa-client-grid]');
      if (!clientGrid) {
        clientGrid = document.createElement('ul');
        clientGrid.className = 'product-grid';
        clientGrid.setAttribute('role', 'list');
        clientGrid.setAttribute('data-aiwa-client-grid', '');
        var container = gridContainerEl();
        if (container) container.appendChild(clientGrid);
      }
      clientGrid.innerHTML = '';

      if (visible.length === 0) {
        var empty = document.createElement('li');
        empty.className = 'aiwa-filter-empty';
        empty.textContent = isAr
          ? 'لا توجد نتائج مطابقة لهذا المزيج — جرّب إزالة أحد الفلاتر.'
          : "No flowers match this combination yet — try removing a filter.";
        clientGrid.appendChild(empty);
      } else {
        var frag = document.createDocumentFragment();
        visible.forEach(function (p) { frag.appendChild(buildCard(p)); });
        clientGrid.appendChild(frag);
      }

      if (headerCountEl) headerCountEl.textContent = visible.length;

      // re-sync heart icons on the newly built cards (existing listener does the work)
      window.dispatchEvent(new Event('aiwa:favorites:change'));
    }

    // Show how many filters are on, e.g. "FILTERS (2)", so a shopper can
    // tell the grid is narrowed after the drawer closes.
    var filtersBtn = document.querySelector('[data-aiwa-panel-mode="filters"]');
    var filtersBtnLabel = filtersBtn ? filtersBtn.textContent.trim() : '';
    function updateFiltersButton() {
      if (!filtersBtn) return;
      var n = FACET_GROUPS.reduce(function (sum, g) { return sum + state[g].length; }, 0);
      filtersBtn.textContent = n ? filtersBtnLabel + ' (' + n + ')' : filtersBtnLabel;
    }

    function refresh() {
      updateCounts();
      renderGrid();
      updateFiltersButton();
    }

    // Registered on `root` in the CAPTURE phase, not per-checkbox in the
    // (default) bubble phase. Reason: collection-ai-filters.js has its own
    // document-level bubble-phase "change" watcher that fires on ANY change
    // inside [data-aiwa-filter-panel] and immediately rebuilds a Shopify
    // filter URL + AJAX-swaps #CollectionAIProductGrid's innerHTML — which
    // would silently wipe out our client-rendered filtered grid the instant
    // a facet checkbox changes. Our checkboxes deliberately have no `name`
    // attribute (not real Shopify filter params) and no `value` attribute
    // (native checkboxes default to "on", which is why an early version of
    // this leaked "?=on" into the URL via that old handler). Capturing here
    // first and stopping propagation keeps that old handler exclusively for
    // Sort radios, which still have real name/value and legitimately need it.
    root.addEventListener('change', function (e) {
      var cb = e.target.closest('input[data-facet-group]');
      if (!cb) return;
      e.stopImmediatePropagation();
      var group = cb.getAttribute('data-facet-group');
      var value = cb.getAttribute('data-facet-value');
      var idx = state[group].indexOf(value);
      if (cb.checked && idx === -1) state[group].push(value);
      if (!cb.checked && idx !== -1) state[group].splice(idx, 1);
      var option = cb.closest('[data-aiwa-facet-option]');
      if (option) option.classList.toggle('is-checked', cb.checked);
      // Data may have loaded before the shopper picked a sort (no filter
      // on, so the server handled it) — re-fetch in the current order.
      refresh();
      if (currentSort() !== dataSort) resortHook(currentSort(), false);
    }, true);

    var resetBtn = document.querySelector('[data-ai-filter-clear]');
    if (resetBtn) {
      resetBtn.addEventListener('click', function () {
        checkboxes.forEach(function (cb) {
          cb.checked = false;
          var option = cb.closest('[data-aiwa-facet-option]');
          if (option) option.classList.remove('is-checked');
        });
        FACET_GROUPS.forEach(function (g) { state[g] = []; });
        refresh();
      });
    }

    // Data now loads lazily (see boot() below) — a shopper who checks a box
    // in the brief window between the panel opening and the fetch resolving
    // toggled it via plain native checkbox behavior, since the change
    // listener above didn't exist yet. Sync state from whatever's already
    // checked so that click isn't silently lost.
    var anyPreChecked = false;
    checkboxes.forEach(function (cb) {
      if (!cb.checked) return;
      anyPreChecked = true;
      var group = cb.getAttribute('data-facet-group');
      var value = cb.getAttribute('data-facet-value');
      if (state[group].indexOf(value) === -1) state[group].push(value);
      var option = cb.closest('[data-aiwa-facet-option]');
      if (option) option.classList.add('is-checked');
    });

    // Sort chosen while a filter is on: re-fetch the product list in the
    // new order and re-render the filtered grid, instead of letting the
    // legacy server swap wipe the filter out.
    // collection-ai-filters.js replaces #CollectionAIProductGrid's children
    // on pill clicks, back/forward and server-side sorts. If a filter is on,
    // that wipes the filtered grid (and the pill may have changed), so rebuild
    // it; either way the option counts need refreshing for the new pill.
    var observedGrid = gridContainerEl();
    if (observedGrid && window.MutationObserver) {
      new MutationObserver(function () {
        if (anyActive && !document.querySelector('[data-aiwa-client-grid]')) {
          originalGridStale = false;
          if (currentSort() !== dataSort) resortHook(currentSort(), false);
          else refresh();
        } else if (!anyActive) {
          updateCounts();
        }
      }).observe(observedGrid, { childList: true });
    }

    // Pill clicks and back/forward update the URL straight away but the
    // server grid only arrives after a fetch; with a filter on, re-render
    // from the data already in memory so the old pill's products don't
    // linger on screen meanwhile.
    function refreshSoon() { if (anyActive) setTimeout(refresh, 0); }
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-aiwa-tagbar] .aiwa-tagbar__item')) refreshSoon();
    });
    window.addEventListener('popstate', refreshSoon);

    resortHook = function (sortValue, markStale) {
      dataSort = sortValue;
      if (markStale !== false) originalGridStale = true;
      return fetchAllProducts().then(function (sorted) {
        if (sorted.length) products = sorted;
        refresh();
      });
    };

    if (anyPreChecked) refresh();
    else updateCounts();
    if (currentSort() !== dataSort) resortHook(currentSort(), false);
  }

  var resortHook = null;

  // ---- guard: "Show Flowers" (.aiwa-apply) also drives Sort, via a
  // pre-existing handler in collection-ai-filters.js that reads every
  // checked input's name/value and does a server AJAX section-swap. Our
  // facet checkboxes deliberately have no name attribute (they're not real
  // Shopify filter params), so that handler would silently ignore them and
  // replace our client-filtered grid with an unfiltered one. When a facet
  // is active, intercept in the capture phase (runs before that bubble-phase
  // handler regardless of script load order) and just close the drawer
  // instead — filtering is already live, there's nothing to "apply".
  // Sort-only clicks (no facet active) are left alone so Sort keeps working
  // exactly as before.
  document.addEventListener('click', function (e) {
    if (!facetsCurrentlyActive && !root.querySelector('input[data-facet-group]:checked')) return;
    if (!e.target.closest('.aiwa-apply')) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    var panel = document.querySelector('[data-aiwa-filter-panel]');
    var overlay = document.querySelector('[data-aiwa-filter-overlay]');
    if (panel) panel.classList.remove('is-open');
    if (overlay) overlay.classList.remove('is-open');
    document.body.classList.remove('aiwa-filter-open');
  }, true);

  // 2026-08-07 — was: fetch the full product set unconditionally on every
  // collection page load (~35-40KB / 2 requests for a 400+ product
  // collection), whether or not the shopper ever opens Filters. Now fetches
  // once, lazily, the first time a FILTERS/SORT BY trigger is interacted
  // with — `mousedown`/`touchstart` (fires slightly before `click`) gives
  // the fetch a small head start over the panel's own open animation, so
  // by the time it's visible the data has often already arrived.
  var bootPromise = null;
  function boot() {
    if (bootPromise) return bootPromise;
    bootPromise = fetchAllProducts().then(function (products) {
      if (products.length) init(products);
      else bootPromise = null; // load failed: try again next time Filters is opened
    });
    return bootPromise;
  }

  document.addEventListener('mousedown', function (e) {
    if (e.target.closest('[data-aiwa-filter-open]')) boot();
  });

  // Facet ticked before the product data finished loading: keep the change
  // away from collection-ai-filters.js's document-level watcher (it would
  // reload the grid and rewrite the URL). init()'s own capture listener on
  // `root` still runs (stopPropagation doesn't block same-node listeners),
  // and init() picks up already-ticked boxes when it boots.
  root.addEventListener('change', function (e) {
    if (!e.target.closest('input[data-facet-group]')) return;
    e.stopPropagation();
    boot();
  }, true);

  // Sort chosen while a filter is active: handled client-side. Registered on
  // the panel in the bubble phase so the panel's own auto-close listener
  // still runs, but the legacy document-level watcher doesn't.
  var sortPanelEl = document.querySelector('[data-aiwa-filter-panel]');
  if (sortPanelEl) {
    sortPanelEl.addEventListener('change', function (e) {
      if (!e.target.matches('input[name="sort_by"]')) return;
      if (!resortHook) return;
      if (!facetsCurrentlyActive) {
        // No filter on: the server re-sorts the grid as before; just re-load
        // our product list in the new order in the background so a filter
        // applied next is already in the right order.
        resortHook(e.target.value, false);
        return;
      }
      e.stopPropagation();
      var params = new URLSearchParams(window.location.search);
      params.set('sort_by', e.target.value);
      params.delete('page');
      window.history.pushState({}, '', window.location.pathname + '?' + params.toString());
      resortHook(e.target.value);
    });
  }
  document.addEventListener('touchstart', function (e) {
    if (e.target.closest('[data-aiwa-filter-open]')) boot();
  }, { passive: true });
})();
